import React, { useEffect, useRef, useState } from 'react';
import { OrderStatusSectionProps } from './order-status-section.props';
import {
  StyledOrderStatusSectionContainer,
  StyledPanelPlaceholder,
  StyledVerticalLine
} from './order-status-section.styled';
import Pagination from 'components/pagination';
import { useLocation } from 'react-router-dom';
import { Flex, Text } from 'common/common-components.styled';
import Switch from 'react-switch';
import { useTheme } from 'styled-components';
import debounce from 'lodash/debounce';
import { Section } from 'pages/existing-vehicle-page/existing-vehicle';
import { PAGES } from 'common/enums';
import useEventDescriptionsUtils from '../../hooks/use-event-descriptions-utils';

const OrderStatusSection = ({ events }: OrderStatusSectionProps) => {
  const location = useLocation();
  const theme = useTheme();
  const statusesContainer = useRef<HTMLDivElement | null>(null);
  const { getEventDescriptionsForStatusSection } = useEventDescriptionsUtils();

  const [paginationParams, setPaginationParams] = useState(defaultPaginationParams);
  const [statusesContainerWidth, setStatusesContainerWidth] = useState(0);
  const [showOnlyDoneEvents, setShowOnlyDoneEvents] = useState(true);

  const isDealerDetailsPage = location.pathname.includes(PAGES.REQUESTS_DETAILS);
  const eventDescriptions = getEventDescriptionsForStatusSection(events, showOnlyDoneEvents, statusesContainerWidth);

  useEffect(() => {
    const handleWindowResize = () => {
      setStatusesContainerWidth(statusesContainer.current?.offsetWidth || 0);
    };
    const handleWindowResizeDebounced = debounce(handleWindowResize, 50);

    handleWindowResizeDebounced();
    window.addEventListener('resize', handleWindowResizeDebounced);

    return () => {
      window.removeEventListener('resize', handleWindowResizeDebounced);
    };
  }, []);

  const pageChangeHandler = (page: number) => {
    setPaginationParams(paginationParams => ({ ...paginationParams, start: page }));
  };

  const toggleShowOnlyDoneEvents = () => {
    setShowOnlyDoneEvents(flag => !flag);
    pageChangeHandler(1);
  };

  if (isDealerDetailsPage && !eventDescriptions.length) {
    return null;
  }

  return (
    <Section>
      <div>
        <Flex justify='space-between' align='center' bottom='50'>
          <Text bold size='22' color='grey800'>
            Status
          </Text>
          <Flex>
            <Text pointer onClick={toggleShowOnlyDoneEvents} right='10'>
              Nur bereits erledigte Positionen anzeigen
            </Text>
            <Switch
              offColor={theme.colors.grey250}
              onColor={theme.colors.blue}
              height={20}
              width={40}
              uncheckedIcon={false}
              checkedIcon={false}
              onChange={toggleShowOnlyDoneEvents}
              checked={showOnlyDoneEvents}
            />
          </Flex>
        </Flex>
        <div ref={statusesContainer}>
          <StyledOrderStatusSectionContainer>
            {eventDescriptions.length ? (
              <>
                {eventDescriptions.slice(
                  paginationParams.start === 1 ? 0 : paginationParams.number * (paginationParams.start - 1),
                  paginationParams.number * paginationParams.start
                )}

                <StyledVerticalLine />
              </>
            ) : (
              <StyledPanelPlaceholder
                title='Keine Statusänderungen'
                description='Die Bestellung enthält noche keine Statusänderungen.'
                children={null}
              />
            )}
          </StyledOrderStatusSectionContainer>

          {eventDescriptions.length > paginationParams.number && (
            <Pagination
              activePage={paginationParams.start}
              itemsCountPerPage={paginationParams.number}
              totalItemsCount={eventDescriptions.length}
              onPageChange={pageChangeHandler}
              noScroll={true}
            />
          )}
        </div>
      </div>
    </Section>
  );
};

const defaultPaginationParams = {
  start: 1,
  number: 10
};

export default OrderStatusSection;
